import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PatientRecord } from "@common/utils/types";
import { updateTableRecordStage } from "@redux/patientTableRecordsSlice";
import type { RootState } from "@/app/store";
import { restoreReviewSession } from "./reviewSessionStorage";

// EN-18 Review Mode: queue snapshot for one review session. Kept in this tab's sessionStorage so a refresh
// resumes it (see reviewSessionStorage.ts); closing the tab, exiting or signing out ends it.
export type ReviewSessionState = {
    records: PatientRecord[];
    currentIndex: number;
    skippedIds: string[];
    accountId: string | null;
    // The signed-in user who started the review. A restored review belongs to them alone.
    userId: string | null;
    active: boolean;
    // True once the user has been seen on a review route; lets the guard tell "not navigated yet" from "left".
    entered: boolean;
    // Pathname the session was started from. Leaving it before entering the flow means the opening
    // navigation never landed, so the guard ends the session instead of leaving it stuck active.
    startPath: string | null;
    // A move (flush, write, navigate) is in flight. Lives here, not in the bar, so a bar remounted by
    // browser back/forward mid-move stays disabled until that move settles.
    moving: boolean;
    // The card row's scroll position, carried across the bar remounts every move causes. Not saved for a refresh.
    rowScrollLeft: number;
};

export const initialReviewSessionState: ReviewSessionState = {
    records: [],
    currentIndex: 0,
    skippedIds: [],
    accountId: null,
    userId: null,
    active: false,
    entered: false,
    startPath: null,
    moving: false,
    rowScrollLeft: 0,
};

const reviewSessionSlice = createSlice({
    name: "reviewSession",
    // A review saved in this tab before a refresh picks up where it was.
    initialState: () => restoreReviewSession() ?? initialReviewSessionState,
    reducers: {
        startReview: (
            _state,
            action: PayloadAction<{
                records: PatientRecord[];
                accountId: string | null;
                userId: string | null;
                startPath: string;
            }>,
        ) => ({
            records: action.payload.records,
            currentIndex: 0,
            skippedIds: [],
            accountId: action.payload.accountId,
            userId: action.payload.userId,
            active: true,
            entered: false,
            startPath: action.payload.startPath,
            moving: false,
            rowScrollLeft: 0,
        }),
        markReviewEntered: (state) => {
            state.entered = true;
        },
        setCurrentIndex: (state, action: PayloadAction<number>) => {
            state.currentIndex = action.payload;
        },
        setRowScrollLeft: (state, action: PayloadAction<number>) => {
            state.rowScrollLeft = action.payload;
        },
        setMoving: (state, action: PayloadAction<boolean>) => {
            state.moving = action.payload;
        },
        markSkipped: (state, action: PayloadAction<string>) => {
            if (!state.skippedIds.includes(action.payload)) state.skippedIds.push(action.payload);
        },
        endReview: () => initialReviewSessionState,
    },
    extraReducers: (builder) => {
        // Keep the queued copy's stage in step with the table so the summary's finalized count is right.
        builder.addCase(updateTableRecordStage, (state, action) => {
            const record = state.records.find((r) => r.id === action.payload.id);
            if (record) record.stage = action.payload.newStage;
        });
    },
});

export const { startReview, setCurrentIndex, setMoving, markSkipped, endReview, markReviewEntered, setRowScrollLeft } =
    reviewSessionSlice.actions;

export const selectReviewSession = (state: RootState) => state.reviewSession;
export const selectReviewActive = (state: RootState): boolean => state.reviewSession.active;
export const selectReviewIndexOf = (state: RootState, id: string): number =>
    state.reviewSession.records.findIndex((r) => r.id === id);

export default reviewSessionSlice.reducer;
